<template>
  <cards :row="true" class="!items-stretch h-full">
    <Card class="w-1/2 h-full">
      <div class="flex flex-col h-full">
        <div class="relative h-full">
          <MultiImage
            :images="images"
            :enlargeable="true"
            :disabled="loadingVerified"
            :enable-rotation="true"
          />
        </div>
        <div class="flex gap-x-10 justify-center mt-10">
          <CustomButton
            variant="outlined"
            :disabled="loadingVerified || isEditing"
            :busy="loadingDeny"
            :class="{ grayed: verification?.status === 'Verified' }"
            @click="update(false)"
          >
            {{ $t('pages.images.verifications.verificationModal.denyButtonText') }}
          </CustomButton>
          <CustomButton
            variant="primary"
            :disabled="loadingVerified || isEditing"
            :busy="loadingAccept"
            :class="{ grayed: verification?.status === 'Denied' }"
            @click="update(true)"
          >
            {{ $t('pages.images.verifications.verificationModal.verifyButtonText') }}
          </CustomButton>
        </div>
      </div>
    </Card>
    <Card class="w-1/2">
      <Readonly
        class="w-full mb-3"
        :label="$t('pages.images.verifications.verificationModal.driver')"
      >
        <RouterLink v-if="driver?.id" :to="{ name: 'driver-details', params: { driverId: driver?.id ?? '' } }">
          {{ !!driver?.user ? `${driver?.user.firstName} ${driver?.user.lastName}` : '' }}
        </RouterLink>
      </Readonly>
      <Readonly
        v-if="!isAdmin"
        class="w-full mb-3"
        :label="$t('pages.images.verifications.verificationModal.campaign')"
      >
        {{ props?.verification?.campaignRegistration?.campaign?.name ?? '' }}
      </Readonly>
      <Readonly
        v-else
        class="w-full mb-3 custom-readonly"
        :class="{ '!border-red': isEditing }"
        :label="$t('pages.images.photos.ratingModal.campaign')"
      >
        <div class="flex gap-x-4 items-center justify-between pr-4">
          <div v-if="!isEditing">
            {{ props?.verification?.campaignRegistration?.campaign?.name ?? '' }}
          </div>
          <div v-else class="grow pr-5">
            <Multiselect
              v-model="selectedCampaignRegistration"
              :options="campaignRegistrations"
              :loading="loadingCampaigns"
              class="w-full !rounded !border border-element-neutral"
              :can-deselect="true"
              :can-clear="true"
              :searchable="true"
            />
          </div>
          <CustomButton
            v-if="!isEditing"
            variant="primary"
            class="py-2 px-3 !text-input"
            @click="startEditing"
          >
            {{ $t('edit') }}
          </CustomButton>
          <template v-else>
            <CustomButton
              variant="outlined"
              class="py-2 px-3 !text-input"
              :disabled="loading2"
              @click="cancelEditing"
            >
              {{ $t('cancel') }}
            </CustomButton>
            <CustomButton
              variant="primary"
              class="py-2 px-3 !text-input"
              :busy="loading2"
              :disabled="loading2"
              @click="saveEditing"
            >
              {{ $t('save') }}
            </CustomButton>
          </template>
        </div>
      </Readonly>
      <Readonly class="w-full mb-3" :label="$t('region')">
        {{ props?.verification?.campaignRegistration?.campaign?.region?.name ?? props?.verification?.driver?.region?.name ?? '' }}
      </Readonly>
      <Readonly class="w-full mb-3" :label="$t('pages.images.photos.ratingModal.license')">
        <RouterLink
          v-if="props.verification?.licenseSeatRegistration?.licenseSeat?.license?.company"
          :to="{name: 'company-license-details',
                params: { companyId: props.verification.licenseSeatRegistration.licenseSeat.license.company.id },
                query: {
                  license: JSON.stringify(props.verification.licenseSeatRegistration.licenseSeat.license['@id']),
                }}"
        >
          {{ formatLicenseSeatName(props.verification.licenseSeatRegistration.licenseSeat) }}
        </RouterLink>
        <span v-else>-</span>
      </Readonly>
      <Readonly class="w-full mb-3" :label="$t('driver.canton')">
        {{
          props?.verification?.driver?.canton
            ? t(`canton.${props?.verification?.driver?.canton}`)
            : ''
        }}
      </Readonly>
      <Readonly
        class="w-full mb-3"
        :label="$t('pages.images.verifications.verificationModal.createdAt')"
      >
        {{
          props?.verification?.createdAt ? format(new Date(props?.verification?.createdAt), "dd.MM.yyyy") : ''
        }}
      </Readonly>
      <Readonly
        class="w-full mb-3"
        :label="$t('pages.images.verifications.verificationModal.oldestFileCreatedAt')"
      >
        {{
          props?.verification?.oldestFileCreatedAt ? format(new Date(props?.verification?.oldestFileCreatedAt), "dd.MM.yyyy") : ''
        }}
      </Readonly>
    </Card>
  </cards>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import Cards from '@utils/Cards.vue';
import { format } from 'date-fns';
import Card from '@utils/Card.vue';
import Readonly from '@utils/Readonly.vue';
import { useVModel } from '@vueuse/core';
import { useFetchAction } from '@hooks/fetch';
import { useI18n } from 'vue-i18n';
import { useNotify } from '@utils/notifications';
import MultiImage from '@utils/MultiImage.vue';
import CustomButton from '@utils/CustomButton.vue';
import { useConfirmPrompt } from '@utils/prompt/injects';
import Multiselect from '@utils/Multiselect.vue';
import { collOptionsProvider } from '@utils/table/hooks/provider';
import type { Option } from '@utils/filter/filterTypes/Dropdown.vue';
import type { Verification, CampaignRegistration, LicenseSeat } from '@/entities/Entities';
import { useStore } from '@/store';

interface Props {
  verification: Verification;
  loadingVerified: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits(['update:verification', 'verified', 'update:loading-verified']);
const internalVerification = useVModel(props, 'verification', emit);

const store = useStore();

const isAdmin = computed(() => store.getters['auth/IS_ADMIN']);

const isEditing = ref(false);

const driver = computed(() => props?.verification?.driver);

const { t } = useI18n();

const notify = useNotify();

const images = computed(() => (props.verification ? [
  {
    file: props.verification.pictureTop,
    title: t('verification.pictureTop'),
  },
  {
    file: props.verification.pictureBack,
    title: t('verification.pictureBack'),
  },
  {
    file: props.verification.pictureLeft,
    title: t('verification.pictureLeft'),
  },
  {
    file: props.verification.pictureRight,
    title: t('verification.pictureRight'),
  },
  {
    file: props.verification.pictureFront,
    title: t('verification.pictureFront'),
  },
] : []));

const status = (accepted: boolean) => (accepted ? 'Verified' : 'Denied');

const [exec, { loading }] = useFetchAction();

const loadingDeny = ref(false);
const loadingAccept = ref(false);

const confirm = useConfirmPrompt();

const save = async (accepted: boolean) => {
  try {
    if (accepted) {
      loadingAccept.value = true;
    } else {
      loadingDeny.value = true;
    }
    await exec(props.verification['@id'], {
      method: 'PUT',
      body: {
        status: status(accepted),
      },
    });
    emit('update:loading-verified', false);
    emit('verified');
    loadingAccept.value = false;
    loadingDeny.value = false;
    return true;
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t('pages.images.verifications.verificationModal.saveError'),
    });
    emit('update:loading-verified', false);
    loadingAccept.value = false;
    loadingDeny.value = false;
    return false;
  }
};

const update = async (accepted: boolean) => {
  if (!accepted) {
    const answer = await confirm?.(t('pages.images.verifications.verificationModal.denyConfirmMessage'));
    if (!answer) {
      return answer ?? false;
    }
  }

  internalVerification.value.status = status(accepted);
  emit('update:loading-verified', true);
  return save(accepted);
};

const campaignRegistrations = ref<Option[]>([]);

const selectedCampaignRegistration = ref();

const loadingCampaigns = ref(false);

const startEditing = async () => {
  if (!props.verification.driver?.id) {
    return;
  }
  isEditing.value = true;
  emit('update:loading-verified', true);
  try {
    loadingCampaigns.value = true;
    campaignRegistrations.value = await collOptionsProvider<CampaignRegistration>(
      '/campaign_registrations',
      {
        filter: {
          driver: props.verification.driver.id,
        },
      },
      (value) => ({
        label: value.campaign.name,
        value: value['@id'],
      }),
    )();
    if (selectedCampaignRegistration.value === undefined) {
      selectedCampaignRegistration.value = props.verification?.campaignRegistration?.['@id'];
    }
    loadingCampaigns.value = false;
  } catch (e) {
    isEditing.value = false;
    emit('update:loading-verified', false);
  }
};

const [exec2, { loading: loading2 }] = useFetchAction();

const saveEditing = async () => {
  try {
    const newPhoto = await exec2(props.verification['@id'], {
      method: 'PUT',
      body: {
        campaignRegistration: selectedCampaignRegistration.value,
      },
    });

    emit('update:verification', newPhoto);

    isEditing.value = false;
    campaignRegistrations.value = [];
    selectedCampaignRegistration.value = undefined;
    emit('update:loading-verified', false);
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t('saveError'),
    });
  }
};

const cancelEditing = async () => {
  isEditing.value = false;
  campaignRegistrations.value = [];
  selectedCampaignRegistration.value = undefined;
  emit('update:loading-verified', false);
};

const formatLicenseSeatName = (licenseSeat?: LicenseSeat) => {
  if (!licenseSeat) return '-';
  const license = licenseSeat?.license;
  if (!license) return '-';
  return `${license.company?.name} - ${license.startDate ? format(new Date(license.startDate), 'dd.MM.yyyy') : ''} - ${license.endDate ? format(new Date(license.endDate), 'dd.MM.yyyy') : ''}`;
};
</script>

<style lang="scss" scoped>
::v-deep(.card-body) {
  height: 100%;
}

::v-deep(img) {
  position: absolute;
  object-fit: contain;
}

.image {
  height: 100%;
}

.grayed:not(:hover) {
  opacity: 0.2;
}
</style>
